import { useTranslation } from 'next-i18next';

import { useCallback } from 'react';

import { GeoJSONStoreFeatures } from 'terra-draw';

import { mapToaster } from '@nocowanie/common-ui/components/molecules/toaster';

import { MimeTypes } from '@app/enums';
import { buildPolygonsFileName, validateImportedPolygons } from '@app/helpers/map-polygons.helpers';
import { useMapSearch } from '@app/hooks/useMapSearch';

export const usePolygonsImportExport = ({
    polygons,
    polygonsJson,
    clear,
    importPolygons,
}: {
    polygons: GeoJSONStoreFeatures[];
    polygonsJson: string;
    clear: () => void;
    importPolygons: (polygons: GeoJSONStoreFeatures[]) => void;
}) => {
    const { t } = useTranslation('common', {
        keyPrefix: 'map.editMode',
    });

    const showToast = useCallback((message: string) => {
        mapToaster.create({ description: message });
    }, []);

    const { searchCriteria } = useMapSearch();

    const handleExport = useCallback(() => {
        const { location, geoId, locationType } = searchCriteria;
        if (!polygons.length || !location || !geoId || !locationType) return;

        let url: string | null = null;

        try {
            const fileName = buildPolygonsFileName(location, locationType, geoId);
            const blob = new Blob([polygonsJson], { type: MimeTypes.json });

            url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
        } catch (err) {
            showToast(t('errors.exportError'));
            console.error('[Map Edit Mode] Export polygons error:', err);
        } finally {
            if (url) {
                URL.revokeObjectURL(url);
            }
        }
    }, [polygons, polygonsJson, searchCriteria]);

    const handleImport = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            if (file.type !== MimeTypes.json) {
                showToast(t('errors.invalidType'));
                clear();
                event.target.value = '';

                return;
            }

            try {
                const text = await file.text();
                const parsed = JSON.parse(text);
                const validated = validateImportedPolygons(parsed);

                if (!validated) {
                    throw new Error('Invalid polygons format');
                }

                importPolygons(validated);
                showToast(t('importSuccessful'));
            } catch (err) {
                console.error('[Map Edit Mode] Error reading file:', err);
                showToast(t('errors.invalidData'));
                clear();
            } finally {
                event.target.value = '';
            }
        },
        [clear, importPolygons, showToast],
    );

    return {
        handleImport,
        handleExport,
    };
};
